import {
  Box,
  Checkbox,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { CategoryConfig } from "../../config/categories-config";

interface ValueItemProps {
  valueText: string;
  checked: boolean;
  isSelected: boolean;
  anchorEl: HTMLElement | null;
  categories: (CategoryConfig & { count: number })[];
  selectedCategory: number;
  onCheck: (checked: boolean) => void;
  onItemClick: () => void;
  onDragStart: (e: React.DragEvent<HTMLDivElement>) => void;
  onOpenMenu: (event: React.MouseEvent<HTMLElement>) => void;
  onCloseMenu: () => void;
  onMoveValue: (targetCategory: number) => void;
  onOpenChart: () => void;
}

export const ValueItem: React.FC<ValueItemProps> = ({
  valueText,
  checked,
  isSelected,
  anchorEl,
  categories,
  selectedCategory,
  onCheck,
  onItemClick,
  onDragStart,
  onOpenMenu,
  onCloseMenu,
  onMoveValue,
  onOpenChart,
}) => {
  return (
    <Box
      draggable
      onDragStart={onDragStart}
      onClick={onItemClick}
      sx={{
        display: "flex",
        alignItems: "center",
        borderBottom: "1px solid #eee",
        py: 1,
        pl: 0.8,
        pr: 2,
        cursor: "grab",
        backgroundColor: isSelected
          ? "rgba(25, 118, 210, 0.08)"
          : "transparent",
        "&:active": {
          cursor: "grabbing",
        },
        "&:hover": {
          backgroundColor: isSelected
            ? "rgba(25, 118, 210, 0.12)"
            : "rgba(0, 0, 0, 0.04)",
        },
      }}
    >
      <Checkbox
        checked={checked}
        onChange={(e) => onCheck(e.target.checked)}
        sx={{ mr: 1 }}
      />
      <Typography sx={{ flex: 1 }}>{valueText}</Typography>
      <Tooltip title="View link chart" arrow>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onOpenChart();
          }}
          sx={{
            mr: 0.5,
            color: "#00838F",
            "&:hover": {
              backgroundColor: "rgba(0, 131, 143, 0.08)",
              color: "#00697C",
              transform: "scale(1.1)",
            },
            transition: "all 0.2s",
          }}
        >
          <AccountTreeIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Move to category" arrow>
        <IconButton
          size="small"
          onClick={onOpenMenu}
          sx={{
            "&:hover": {
              backgroundColor: "action.hover",
              transform: "scale(1.1)",
            },
            transition: "all 0.2s",
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onCloseMenu}
        slotProps={{
          paper: {
            sx: {
              minWidth: 200,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            },
          },
        }}
      >
        <MenuItem
          disabled
          sx={{
            fontSize: "0.75rem",
            fontWeight: 700,
            color: "text.secondary",
          }}
        >
          Move to:
        </MenuItem>
        {categories
          .filter((cat) => cat.categoryId !== selectedCategory)
          .map((cat) => (
            <MenuItem
              key={cat.categoryId}
              onClick={() => onMoveValue(cat.categoryId)}
              sx={{
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>{cat.icon}</ListItemIcon>
              <ListItemText primary={cat.label} />
              <Chip
                label={cat.count}
                size="small"
                sx={{ ml: 1, fontWeight: 600 }}
              />
            </MenuItem>
          ))}
      </Menu>
    </Box>
  );
};

const ValueItemComponent = ValueItem;
export default ValueItemComponent;
